import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FetchData, { CreateEntry, GetOne } from "../../providers/dataProvider";
import { formatDate, FormatValueDecimal } from "../../utils/Validate";
import ErrorMessage from "../../components/common/ErrorMessage";
import SaveSuccessModel from "../../components/common/SaveSuccessModel";
import RejectedModal from "../../components/common/RejectedModal";
import "./FactoryOrder.scss";
import ViewOrderSummary from "./ViewOrderSummary";
import ViewAccountSummary from "./ViewAccountSummary";
import ViewOrderCharges from "./ViewOrderCharges";

const getErrorPayload = (message) => ({
  errors: [{ message: typeof message === "string" ? message : "Something went wrong" }],
});

const asArray = (value) => {
  if (Array.isArray(value?.payload)) return value.payload;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value)) return value;
  return [];
};

const FactoryOrderApprovalView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const remarksRef = useRef(null);
  const generatedProductionBatchIds = useRef(new Set());
  const approval = location?.state?.approval;
  const factoryReleaseId =
    location?.state?.id ||
    approval?.aggregateId ||
    approval?.factoryReleaseId ||
    approval?.factoryOrderId;

  const [factoryOrderResponse, setFactoryOrderResponse] = useState(null);
  const [factoryOrder, setFactoryOrder] = useState(approval?.factoryOrder || null);
  const [selectedDecision, setSelectedDecision] = useState("");
  const [remarks, setRemarks] = useState("");
  const [remarksError, setRemarksError] = useState("");
  const [decisionMsg, setDecisionMsg] = useState(null);
  const [successShow, setSuccessShow] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [errorShow, setErrorShow] = useState({
    show: false,
    errorMessage: "",
  });

  const isFinalApproval = (approvalDecision) =>
    String(approvalDecision?.status || approvalDecision?.Status || "").toLowerCase() === "approved";

  const generateProductionBatch = (onComplete) => {
    const releaseKey = String(factoryReleaseId);
    if (!factoryReleaseId || generatedProductionBatchIds.current.has(releaseKey)) return;

    // Set this before the request so a repeated click cannot create another batch.
    generatedProductionBatchIds.current.add(releaseKey);
    CreateEntry(
      "factoryrelease",
      { factoryReleaseId },
      onComplete,
      "Production batch generated successfully",
      "Failed to generate production batch"
    );
  };
  const { data: orderDataList } = FetchData("order", {
    pageNumber: 1,
    pageSize: 100,
  });
  const { data: projectData } = FetchData("project");
  const { data: customerData } = FetchData("Customer");
  const { data: currencyData } = FetchData("currency");
  const { data: assignedEngineerList } = FetchData("userprofile");

  const relatedOrder = useMemo(() => {
    const orders = asArray(orderDataList);
    const customerOrderId = factoryOrder?.customerOrderId || factoryOrder?.orderId;
    const customerOrderReference = factoryOrder?.customerOrderReference;
    const hasCustomerOrderId = customerOrderId !== null && customerOrderId !== undefined && customerOrderId !== "";

    return orders.find((item) =>
      (
        hasCustomerOrderId &&
        (
          String(item?.id) === String(customerOrderId) ||
          String(item?.customerOrderId) === String(customerOrderId) ||
          String(item?.orderId) === String(customerOrderId)
        )
      ) ||
      (
        customerOrderReference &&
        String(item?.customerOrderReference || item?.orderReference) === String(customerOrderReference)
      )
    );
  }, [factoryOrder?.customerOrderId, factoryOrder?.customerOrderReference, factoryOrder?.orderId, orderDataList]);

  const projectDetails = useMemo(
    () => asArray(projectData).find((item) => String(item?.id) === String(relatedOrder?.projectId)),
    [projectData, relatedOrder?.projectId]
  );

  const customerDetails = useMemo(
    () => asArray(customerData).find((item) => String(item?.id) === String(relatedOrder?.customerId)),
    [customerData, relatedOrder?.customerId]
  );

  const currencyDetails = useMemo(
    () => asArray(currencyData).find((item) => String(item?.id) === String(factoryOrder?.currencyId || relatedOrder?.currencyId)),
    [currencyData, factoryOrder?.currencyId, relatedOrder?.currencyId]
  );

  const factoryOrderCurrency =
    currencyDetails?.shortCode ||
    currencyDetails?.currencyName ||
    factoryOrder?.currencyName ||
    factoryOrder?.currencyCode ||
    relatedOrder?.currencyName ||
    "-";
  const conversionRate = factoryOrder?.conversionRate ?? relatedOrder?.conversionRate;
  const factoryReleaseValue = factoryOrder?.factoryReleaseValue;
  const formattedFactoryReleaseValue = factoryReleaseValue === null || factoryReleaseValue === undefined || factoryReleaseValue === ""
    ? "-"
    : `${factoryOrderCurrency} ${FormatValueDecimal(factoryReleaseValue)}`;

  const assignedEngineerDetails = useMemo(
    () => asArray(assignedEngineerList).find((item) => String(item?.identityId) === String(relatedOrder?.salesEngineerId)),
    [assignedEngineerList, relatedOrder?.salesEngineerId]
  );

  const engineerName = [assignedEngineerDetails?.firstName, assignedEngineerDetails?.lastName]
    .filter(Boolean)
    .join(" ");
  const customerOrderReference =
    relatedOrder?.customerOrderReference ||
    relatedOrder?.orderReference ||
    factoryOrder?.customerOrderReference ||
    factoryOrder?.orderReference ||
    "-";

  useEffect(() => {
    const cleanupNotificationDrawers = () => {
      const $ = window.jQuery || window.$;

      ["factoryApprovalRightSideModalPopup", "notificationRightSideModalPopup"].forEach((modalId) => {
        const modal = window.document.getElementById(modalId);
        if (!modal) return;

        if ($) {
          $(modal).modal("hide");
        } else {
          modal.classList.remove("show");
          modal.style.display = "none";
          modal.setAttribute("aria-hidden", "true");
          modal.removeAttribute("aria-modal");
        }
      });

      if ($) {
        $(document).off("focusin.bs.modal");
      }

      window.document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove());
      window.document.body.classList.remove("modal-open");
      window.document.body.style.removeProperty("padding-right");
    };

    cleanupNotificationDrawers();

    window.setTimeout(() => remarksRef.current?.focus(), 0);
  }, []);

  useEffect(() => {
    if (!factoryReleaseId) {
      navigate("/factory-order-approval");
      return;
    }

    GetOne("factoryOrder", { id: factoryReleaseId }, setFactoryOrderResponse);
  }, [factoryReleaseId, navigate]);

  useEffect(() => {
    if (factoryOrderResponse?.message === "success") {
      setFactoryOrder(factoryOrderResponse?.data);
    }
  }, [factoryOrderResponse]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("pathfinderHeaderMetaChange", {
      detail: {
        pathname: location.pathname,
        title: `Factory Order Approval : ${approval?.referenceNo || factoryOrder?.factoryReleaseReference || ""}`,
        backTarget: { pathname: "/factory-order-approval" },
      },
    }));

    return () => {
      window.dispatchEvent(new CustomEvent("pathfinderHeaderMetaChange", {
        detail: {
          pathname: location.pathname,
          clear: true,
        },
      }));
    };
  }, [approval?.referenceNo, factoryOrder?.factoryReleaseReference, location.pathname]);

  useEffect(() => {
    if (!decisionMsg) return;

    setDecisionLoading(false);
    if (decisionMsg?.status === 200) {
      setSuccessShow(true);
      window.dispatchEvent(new Event("approvalStatusChanged"));
    } else {
      setErrorShow({
        show: true,
        errorMessage: decisionMsg?.successData || getErrorPayload(decisionMsg?.message || "Failed to submit approval decision"),
      });
    }
  }, [decisionMsg]);

  const submitDecision = () => {
    if (!factoryReleaseId || decisionLoading || !selectedDecision) return;

    const isApproved = selectedDecision === "approve";
    if (!isApproved && !remarks.trim()) {
      setRemarksError("Remarks are required");
      remarksRef.current?.focus();
      return;
    }

    setDecisionLoading(true);
    CreateEntry(
      "factoryApprovalDecision",
      {
        id: factoryReleaseId,
        isApproved,
        remarks: remarks || (isApproved ? "approved" : "rejected"),
      },
      (response) => {
        setDecisionMsg(response);

        // The API returns the aggregate approval status. It is Approved only
        // after the last required approver has completed the request.
        if (isApproved && response?.status === 200 && isFinalApproval(response?.successData)) {
          generateProductionBatch((generationResponse) => {
            if (generationResponse?.status !== 200) {
              setErrorShow({
                show: true,
                errorMessage: generationResponse?.successData || getErrorPayload("Failed to generate production batch"),
              });
            }
          });
        }
      },
      isApproved ? "Approved successfully" : "Rejected successfully",
      isApproved ? "Failed to approve" : "Failed to reject"
    );
  };

  const successDoneClick = () => {
    setSuccessShow(false);
    navigate("/factory-order-approval");
  };

  const errorDoneClick = () => {
    setErrorShow({ show: false, errorMessage: "" });
    setDecisionMsg(null);
  };

  // const openFactoryOrderEdit = () => {
  //   if (!factoryReleaseId) return;

  //   window.open(
  //     `/add-new-factory-order?id=${encodeURIComponent(factoryReleaseId)}`,
  //     "_blank",
  //   );
  // };

  const openFactoryOrderEdit = () => {
  if (!factoryReleaseId) return;

  const state = {
    id: factoryReleaseId,
    approval,
    fromApproval: true,
  };

  sessionStorage.setItem(
    `factoryOrderEdit_${factoryReleaseId}`,
    JSON.stringify(state)
  );

  const url = `/add-new-factory-order?id=${encodeURIComponent(
    factoryReleaseId
  )}&tab=order-summary`;

  window.open(url, "_blank");
};

  return (
    <div className="container-fluid view-order-sec pl-3 pr-3">
      <section className="view-sec">
        <div className="row mt-2">
          <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12">
          

            <div className="card">
              <div className="card-header">
                <h1>General</h1>
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-4 col-sm-6 col-xs-12">
                    <p>Factory Release Reference</p>
                    <h6>{factoryOrder?.factoryReleaseReference || approval?.referenceNo || "-"}</h6>
                  </div>
                  <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-4 col-sm-6 col-xs-12">
                      <p>Customer</p>
                      <h6>{customerDetails?.customerName || relatedOrder?.customerName || factoryOrder?.customerName || "-"}</h6>
                  </div>
                  <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-4 col-sm-6 col-xs-12">
                    <p>Customer Order Reference</p>
                    <h6>{customerOrderReference}</h6>
                  </div>
                  <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-4 col-sm-6 col-xs-12">
                    <p>Status</p>
                    <h6>{approval?.status || factoryOrder?.approval?.status || "-"}</h6>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-4 col-sm-6 col-xs-12">
                    <p>Requested By</p>
                    <h6>{approval?.requesterName || factoryOrder?.approval?.requesterName || "-"}</h6>
                  </div>
                  <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-4 col-sm-6 col-xs-12">
                    <p>Requested At</p>
                    <h6>{formatDate(approval?.requestedAt || factoryOrder?.approval?.requestedAt) || "-"}</h6>
                  </div>
                  <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-4 col-sm-6 col-xs-12">
                    <p>Scheduled Delivery Date</p>
                    <h6>{formatDate(factoryOrder?.scheduledDeliveryDate) || "-"}</h6>
                  </div>
                  <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-4 col-sm-6 col-xs-12">
                    <p>Project</p>
                    <h6>{projectDetails?.projectName || relatedOrder?.projectName || factoryOrder?.projectName || "-"}</h6>
                  </div>
                 </div>
                 <div className="row mb-3">
                   <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-4 col-sm-6 col-xs-12">
                          <p>Factory Release Value</p>
                          <h6>{formattedFactoryReleaseValue}</h6>
                    </div>
                    <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-4 col-sm-6 col-xs-12">
                      <p>Conversion Rate</p>
                      <h6>{conversionRate ?? "-"}</h6>
                    </div>
                    <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-4 col-sm-6 col-xs-12">
                      <p>Sales Engineer</p>
                      <h6>{engineerName || relatedOrder?.salesEngineerName || factoryOrder?.salesEngineerName || "-"}</h6>
                    </div>
                    <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-4 col-sm-6 col-xs-12">
                      <h6>
                        <button
                          type="button"
                          className="btn btn-sm update-btn"
                          onClick={openFactoryOrderEdit}
                          disabled={!factoryReleaseId}
                          title="View order"
                          style={{ color: "#fff", background: "#005795", borderColor: "#005795" }}
                        >
                          <i className="far fa-eye mr-1"></i>
                          View Order
                        </button>
                      </h6>
                    </div>
                 </div>


                <div className="table-responsive d-none">
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <td>
                          <p>Factory Release Reference</p>
                          <h6>{factoryOrder?.factoryReleaseReference || approval?.referenceNo || "-"}</h6>
                        </td>
                        <td>
                          <p>Customer</p>
                          <h6>{customerDetails?.customerName || relatedOrder?.customerName || factoryOrder?.customerName || "-"}</h6>
                        </td>
                        <td>
                          <p>Customer Order Reference</p>
                          <h6>{customerOrderReference}</h6>
                        </td>
                        <td>
                          <p>Status</p>
                          <h6>{approval?.status || factoryOrder?.approval?.status || "-"}</h6>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <p>Requested By</p>
                          <h6>{approval?.requesterName || factoryOrder?.approval?.requesterName || "-"}</h6>
                        </td>
                        <td>
                          <p>Requested At</p>
                          <h6>{formatDate(approval?.requestedAt || factoryOrder?.approval?.requestedAt) || "-"}</h6>
                        </td>
                        <td>
                          <p>Scheduled Delivery Date</p>
                          <h6>{formatDate(factoryOrder?.scheduledDeliveryDate) || "-"}</h6>
                        </td>
                        <td>
                          <p>Project</p>
                          <h6>{projectDetails?.projectName || relatedOrder?.projectName || factoryOrder?.projectName || "-"}</h6>
                        </td>
                        {/* <td>
                          <p>Delivery To Date</p>
                          <h6>{formatDate(factoryOrder?.deliveryToDate) || "-"}</h6>
                        </td> */}
                      </tr>
                      <tr>
                        {/* <td>
                          <p>Request Remarks</p>
                          <h6>{approval?.requestRemarks || factoryOrder?.approval?.requestRemarks || "-"}</h6>
                        </td> */}
                        
                        <td>
                          <p>Factory Release Value</p>
                          <h6>{formattedFactoryReleaseValue}</h6>
                        </td>
                        <td>
                          <p>Conversion Rate</p>
                          <h6>{conversionRate ?? "-"}</h6>
                        </td>
                        <td>
                          <p>Sales Engineer</p>
                          <h6>{engineerName || relatedOrder?.salesEngineerName || factoryOrder?.salesEngineerName || "-"}</h6>
                        </td>
                        <td>
                          <h6>
                            <button
                              type="button"
                              className="btn btn-sm update-btn"
                              onClick={openFactoryOrderEdit}
                              disabled={!factoryReleaseId}
                              title="View order"
                              style={{ color: "#fff", background: "#005795", borderColor: "#005795" }}
                            >
                              <i className="far fa-eye mr-1"></i>
                              View Order
                            </button>
                          </h6>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
 
            {/* <div className="card my-2">
              <ViewOrderSummary orderSummary={factoryOrder?.products} />
            </div>
           <div className="card mb-2 order-charges-card">
          <ViewOrderCharges viewItem={factoryOrder} />
        </div> */}
             <div className="card mt-2">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h1>Approval Decision</h1>

              </div>
              <div className="card-body">
                <label htmlFor="factory-approval-decision">Decision</label>
                <select
                  id="factory-approval-decision"
                  className="form-control"
                  value={selectedDecision}
                  disabled={decisionLoading}
                  onChange={(event) => {
                    setSelectedDecision(event.target.value);
                    setRemarks("");
                    setRemarksError("");
                  }}
                >
                  <option value="">Select an approval decision</option>
                  <option value="approve">Approve</option>
                  <option value="reject">Reject</option>
                </select>

                {selectedDecision && (
                  <>
                    <label className="mt-3" htmlFor="factory-approval-remarks">
                      Remarks{selectedDecision === "reject" ? " *" : ""}
                    </label>
                    <textarea
                      ref={remarksRef}
                      id="factory-approval-remarks"
                      className="form-control"
                      rows={4}
                      placeholder="Enter approval remarks"
                      value={remarks}
                      aria-invalid={Boolean(remarksError)}
                      onChange={(event) => {
                        setRemarks(event.target.value);
                        if (remarksError) setRemarksError("");
                      }}
                    />
                    {remarksError && <p className="form-error mb-0" style={{color: "red"}}>{remarksError}</p>}
                    <div className="factory-approval-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={decisionLoading}
                        onClick={() => {
                          setSelectedDecision("");
                          setRemarks("");
                          setRemarksError("");
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className={`btn ${selectedDecision === "approve" ? "btn-success" : "btn-danger"}`}
                        disabled={decisionLoading}
                        onClick={submitDecision}
                      >
                        Save
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
           
          </div>
        </div>
      </section>
      {decisionMsg?.message === "Rejected successfully" ? (
        <RejectedModal
          show={successShow}
          createMsg={decisionMsg}
          setShow={setSuccessShow}
          SuccessDoneClick={successDoneClick}
        />
      ) : (
        <SaveSuccessModel
          show={successShow}
          createMsg={decisionMsg}
          setShow={setSuccessShow}
          SuccessDoneClick={successDoneClick}
        />
      )}
      <ErrorMessage
        Show={errorShow?.show}
        errorMessage={errorShow?.errorMessage}
        ErrorDoneClick={errorDoneClick}
      />
    </div>
  );
};

export default FactoryOrderApprovalView;
